import {FC, MouseEventHandler, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Box} from "@interstate/components/Box";
import {ChevronLeftIcon, ChevronRightIcon} from "@interstate/components/Icons";
import {Button} from "@interstate/components/Button";
import {IconType} from "../lib/interstate";
import {useItemIsVisible, VisibleItem} from "../visibility";
import {useStickyState} from "../hooks";
import "./Sidebar.scss";

export interface SidebarItem extends VisibleItem {
  id: string;
  active?: boolean;
  labelKey: string;
  icon: IconType;
  onClick: MouseEventHandler;
}

export interface SidebarProps {
  expanded?: boolean;
  items: SidebarItem[];
}

function itemIsDefined(item?: SidebarItem): item is SidebarItem {
  return item !== undefined;
}

export const Sidebar: FC<SidebarProps> = ({expanded = false, items}) => {
  const itemIsVisible = useItemIsVisible<SidebarItem>();
  const visibleItems = useMemo<SidebarItem[]>(() => {
    return items.filter(itemIsDefined).filter(itemIsVisible);
  }, [itemIsVisible, items]);
  const {t} = useTranslation();
  const [collapsed, setCollapsed] = useStickyState<boolean>(
    !expanded,
    "coat2.sidebar.collapsed"
  );

  const classes = collapsed
    ? "coat-sidebar--collapsed"
    : "coat-sidebar--expanded";

  return (
    <Box
      component={"nav"}
      id={"coat-sidebar"}
      data-testid={"coat-sidebar"}
      className={classes}>
      <Box component={"ul"} className={"coat-sidebar-items"}>
        {visibleItems.map(item => {
          const itemClasses = `coat-sidebar-item ${
            item.active
              ? "coat-sidebar-item--active"
              : "coat-sidebar-item--inactive"
          }`;
          const linkId = `coat-sidebar-item-link-${item.id}`;
          const Icon = item.icon;
          const label = t(item.labelKey, "???");
          return (
            <li key={item.id} className={itemClasses} onClick={item.onClick}>
              <Button
                id={linkId}
                data-testid={linkId}
                className={"coat-sidebar-item-link"}
                buttonStyle={"tertiary"}
                block={true}
                startIcon={<Icon title={label} />}>
                {!collapsed && (
                  <span className={"coat-sidebar-item-label"}>{label}</span>
                )}
              </Button>
            </li>
          );
        })}
      </Box>
      <Button
        id={"coat-sidebar-control"}
        data-testid={"coat-sidebar-control"}
        size={"small"}
        buttonStyle={"secondary"}
        startIcon={
          collapsed ? (
            <ChevronRightIcon
              viewBox={"-3 -2 20 20"}
              title={t("navigation.expand", "???") || "???"}
            />
          ) : (
            <ChevronLeftIcon
              viewBox={"-1 -2 20 20"}
              title={t("navigation.collapse", "???") || "???"}
            />
          )
        }
        onClick={() => setCollapsed(value => !value)}
      />
    </Box>
  );
};
